/**
 * 4Phones In-Cart Indicator (border only)
 * Adds the class `is--in-cart` to any <product-card> whose product is
 * currently in the cart, and removes it when the product leaves the cart.
 * The green border itself is pure CSS (see 4phones.in-cart-indicator.css)
 * reacting to that class. Matches by product_id, so a card variant that
 * differs from the cart variant still highlights correctly.
 *
 * No hover popup / badge / quantity logic — border only.
 */

(function () {
  "use strict";

  let cartProductMap = new Map();
  let inflightFetch = null;

  function buildMapFromItems(items) {
    const next = new Map();
    (items || []).forEach((item) => {
      const pid = String(item.product_id);
      next.set(pid, (next.get(pid) || 0) + item.quantity);
    });
    return next;
  }

  function applyToCard(card) {
    if (!card || !card.dataset) return;
    const productId = card.dataset.productId;
    if (!productId) return;

    const inCart = (cartProductMap.get(String(productId)) || 0) > 0;
    card.classList.toggle("is--in-cart", inCart);
  }

  function applyToAllCards(root) {
    const scope = root && root.querySelectorAll ? root : document;
    scope
      .querySelectorAll("product-card[data-product-id]")
      .forEach(applyToCard);
  }

  function refreshCartMap() {
    if (inflightFetch) return inflightFetch;

    inflightFetch = fetch("/cart.js", {
      credentials: "same-origin",
      headers: { Accept: "application/json" },
    })
      .then((res) => {
        if (!res.ok) throw new Error("cart.js " + res.status);
        return res.json();
      })
      .then((cart) => {
        cartProductMap = buildMapFromItems(cart.items);
        applyToAllCards();
      })
      .catch((err) => {
        // Clear on failure — avoid stale "in cart" borders.
        console.warn("[in-cart-indicator] /cart.js fetch failed", err);
        cartProductMap = new Map();
        applyToAllCards();
      })
      .finally(() => {
        inflightFetch = null;
      });

    return inflightFetch;
  }

  function tryHandleEventPayload(payload) {
    const cartData = payload && payload.cartData;
    if (cartData && Array.isArray(cartData.items)) {
      cartProductMap = buildMapFromItems(cartData.items);
      applyToAllCards();
      return true;
    }
    return false;
  }

  // React to the theme's pub-sub cart updates (add / change / remove).
  function bindCartEvents() {
    if (typeof subscribe !== "function" || typeof PUB_SUB_EVENTS === "undefined") {
      window.addEventListener("load", bindCartEvents, { once: true });
      return;
    }
    subscribe(PUB_SUB_EVENTS.cartUpdate, (payload) => {
      if (!tryHandleEventPayload(payload)) refreshCartMap();
    });
  }

  // Re-apply to cards injected after load (collection filtering, slider, etc.).
  function observeNewCards() {
    if (!("MutationObserver" in window)) return;

    const target = document.getElementById("MainContent") || document.body;
    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        m.addedNodes.forEach((node) => {
          if (node.nodeType !== 1) return;
          if (node.matches && node.matches("product-card[data-product-id]")) {
            applyToCard(node);
          } else if (node.querySelectorAll) {
            applyToAllCards(node);
          }
        });
      }
    });
    observer.observe(target, { childList: true, subtree: true });
  }

  // Safety net for cart paths that skip pub-sub; debounced.
  function observeCartBubble() {
    if (!("MutationObserver" in window)) return;

    const bind = () => {
      const bubble = document.getElementById("cart-icon-bubble");
      if (!bubble) return false;

      let timer = null;
      const observer = new MutationObserver(() => {
        clearTimeout(timer);
        timer = setTimeout(refreshCartMap, 150);
      });
      observer.observe(bubble, {
        childList: true,
        subtree: true,
        characterData: true,
      });
      return true;
    };

    if (!bind()) window.addEventListener("load", bind, { once: true });
  }

  function init() {
    bindCartEvents();
    observeNewCards();
    observeCartBubble();
    refreshCartMap();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
